import { useState, useEffect } from "react";
import axios from "axios";
import { Layers, Calendar, Tag, Clock, CheckCircle2, Circle, AlertTriangle, ArrowUpRight, ChevronRight, ExternalLink, Loader2, Lock, KeyRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API } from "@/utils/constants";

const priorityConfig = {
  high: { color: "text-red-500", bg: "bg-red-500/10", label: "High" },
  medium: { color: "text-amber-500", bg: "bg-amber-500/10", label: "Medium" },
  low: { color: "text-blue-500", bg: "bg-blue-500/10", label: "Low" },
};

const PoweredByFooter = () => (
  <div className="max-w-4xl mx-auto px-6 pb-10" data-testid="shared-powered-by">
    <div className="rounded-2xl border bg-gradient-to-r from-primary/10 to-purple-500/5 px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
      <p className="text-sm text-muted-foreground text-center sm:text-left">
        <span className="font-semibold text-foreground">Powered by Opero Smart</span> — AI-powered project management with boards, chat and CRM.
      </p>
      <Button asChild size="sm" data-testid="shared-powered-by-cta">
        <a href="/?utm_source=shared_view">Sign up free<ArrowUpRight className="h-3.5 w-3.5 ml-1.5" /></a>
      </Button>
    </div>
  </div>
);

const SharedTaskCard = ({ task, columns, categories, tags }) => {
  const column = columns.find(c => c.id === task.column_id);
  const category = categories.find(c => c.id === task.category_id);
  const taskTags = tags.filter(t => task.tag_ids?.includes(t.id));
  const priority = priorityConfig[task.priority] || priorityConfig.medium;
  const completedSubtasks = task.subtasks?.filter(s => s.completed).length || 0;
  const totalSubtasks = task.subtasks?.length || 0;

  return (
    <div className="bg-card border border-border rounded-xl p-5 hover:shadow-md transition-shadow" data-testid={`shared-task-${task.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {task.completed ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0" />
          ) : (
            <Circle className="h-5 w-5 text-muted-foreground shrink-0" />
          )}
          <h3 className={`font-semibold text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.title}</h3>
        </div>
        <Badge variant="outline" className={`${priority.bg} ${priority.color} border-0 text-xs shrink-0`}>
          {priority.label}
        </Badge>
      </div>

      {task.description && (
        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{task.description}</p>
      )}

      <div className="flex flex-wrap gap-2 mb-3">
        {column && (
          <Badge variant="secondary" className="text-xs" style={{ backgroundColor: `${column.color}20`, color: column.color }}>
            {column.name}
          </Badge>
        )}
        {category && (
          <Badge variant="secondary" className="text-xs" style={{ backgroundColor: `${category.color}15`, color: category.color }}>
            {category.name}
          </Badge>
        )}
        {taskTags.map(t => (
          <Badge key={t.id} variant="secondary" className="text-xs" style={{ backgroundColor: `${t.color}15`, color: t.color }}>
            {t.name}
          </Badge>
        ))}
      </div>

      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {task.due_date && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {new Date(task.due_date).toLocaleDateString()}
          </span>
        )}
        {totalSubtasks > 0 && (
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" />
            {completedSubtasks}/{totalSubtasks}
          </span>
        )}
        {task.total_time_minutes > 0 && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {Math.floor(task.total_time_minutes / 60)}h {task.total_time_minutes % 60}m
          </span>
        )}
      </div>
    </div>
  );
};

const SharedTaskDetail = ({ task, columns, categories, tags }) => {
  const column = columns.find(c => c.id === task.column_id);
  const category = categories.find(c => c.id === task.category_id);
  const taskTags = tags.filter(t => task.tag_ids?.includes(t.id));
  const priority = priorityConfig[task.priority] || priorityConfig.medium;

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-card border border-border rounded-2xl p-8" data-testid="shared-task-detail">
        <div className="flex items-center gap-3 mb-6">
          {task.completed ? (
            <CheckCircle2 className="h-7 w-7 text-emerald-500" />
          ) : (
            <Circle className="h-7 w-7 text-muted-foreground" />
          )}
          <h1 className={`text-2xl font-bold ${task.completed ? 'line-through text-muted-foreground' : ''}`}>{task.title}</h1>
        </div>

        {task.description && (
          <p className="text-muted-foreground mb-6 leading-relaxed">{task.description}</p>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          <Badge variant="outline" className={`${priority.bg} ${priority.color} border-0`}>{priority.label} Priority</Badge>
          {column && <Badge style={{ backgroundColor: `${column.color}20`, color: column.color }}>{column.name}</Badge>}
          {category && <Badge style={{ backgroundColor: `${category.color}15`, color: category.color }}>{category.name}</Badge>}
          {taskTags.map(t => <Badge key={t.id} style={{ backgroundColor: `${t.color}15`, color: t.color }}>{t.name}</Badge>)}
        </div>

        <div className="space-y-4">
          {task.due_date && (
            <div className="flex items-center gap-3 text-sm">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span>Due: {new Date(task.due_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          )}

          {task.subtasks?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Subtasks ({task.subtasks.filter(s => s.completed).length}/{task.subtasks.length})</h3>
              <div className="space-y-1.5">
                {task.subtasks.map(s => (
                  <div key={s.id} className="flex items-center gap-2 text-sm">
                    {s.completed ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <Circle className="h-4 w-4 text-muted-foreground" />}
                    <span className={s.completed ? 'line-through text-muted-foreground' : ''}>{s.title}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {task.comments?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2">Comments ({task.comments.length})</h3>
              <div className="space-y-2">
                {task.comments.map(c => (
                  <div key={c.id} className="bg-muted/50 rounded-lg p-3 text-sm">
                    <p>{c.text}</p>
                    <span className="text-xs text-muted-foreground mt-1 block">
                      {new Date(c.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SharedView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);

  const token = window.location.pathname.split('/').pop();

  const fetchShared = async (pw = null) => {
    setLoading(true);
    setError(null);
    try {
      const params = pw ? { password: pw } : {};
      const r = await axios.get(`${API}/shared/${token}`, { params });
      if (r.data.requires_password) {
        setNeedsPassword(true);
        setLoading(false);
        return;
      }
      setData(r.data);
      setNeedsPassword(false);
    } catch (e) {
      if (e.response?.status === 403) {
        setPasswordError(true);
        setNeedsPassword(true);
      } else if (e.response?.status === 410) {
        setError("This share link has expired.");
      } else {
        setError(e.response?.status === 404 ? "This share link is no longer active or doesn't exist." : "Failed to load shared content.");
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!token) { setError("Invalid share link"); setLoading(false); return; }
    fetchShared();
  }, []);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError(false);
    fetchShared(password);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading shared content...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center max-w-md" data-testid="shared-error">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Link Not Found</h2>
          <p className="text-muted-foreground mb-6">{error}</p>
          <a href="/" className="text-primary hover:underline flex items-center gap-1 justify-center">
            <ArrowUpRight className="h-4 w-4" /> Go to Opero Smart
          </a>
        </div>
      </div>
    );
  }

  if (needsPassword && !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-sm" data-testid="shared-password-prompt">
          <div className="bg-card border rounded-2xl p-8 text-center">
            <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Password Required</h2>
            <p className="text-sm text-muted-foreground mb-6">This shared content is protected. Enter the password to view.</p>
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <Input
                type="password"
                placeholder="Enter password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(false); }}
                data-testid="share-password-input"
              />
              {passwordError && <p className="text-sm text-destructive">Incorrect password. Please try again.</p>}
              <Button type="submit" className="w-full" data-testid="share-password-submit">
                <KeyRound className="h-4 w-4 mr-2" />Unlock
              </Button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  if (data?.type === "task") {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/80 backdrop-blur-lg sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">Opero Smart</h1>
              <Badge variant="outline" className="text-xs">Shared Task</Badge>
            </div>
            <span className="text-xs text-muted-foreground">Read-only view</span>
          </div>
        </header>
        <div className="max-w-4xl mx-auto p-6">
          <SharedTaskDetail task={data.task} columns={data.columns} categories={data.categories} tags={data.tags} />
        </div>
        <PoweredByFooter />
      </div>
    );
  }

  if (data?.type === "project") {
    const project = data.project;
    const columns = data.columns || [];
    const tasksByColumn = {};
    columns.forEach(c => { tasksByColumn[c.id] = data.tasks.filter(t => t.column_id === c.id); });
    const unassigned = data.tasks.filter(t => !t.column_id || !columns.find(c => c.id === t.column_id));
    const completedCount = data.tasks.filter(t => t.completed).length;

    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border bg-card/80 backdrop-blur-lg sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">Opero Smart</h1>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold">{project.name}</span>
              <Badge variant="outline" className="text-xs">Shared Project</Badge>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{data.tasks.length} tasks</span>
              <span>{completedCount} completed</span>
              <span className="text-xs">Read-only view</span>
            </div>
          </div>
        </header>
        <div className="max-w-7xl mx-auto p-6" data-testid="shared-project-view">
          {project.description && (
            <p className="text-muted-foreground mb-6">{project.description}</p>
          )}

          <div className="flex gap-6 overflow-x-auto pb-4">
            {columns.map(col => {
              const colTasks = tasksByColumn[col.id] || [];
              return (
                <div key={col.id} className="min-w-[320px] max-w-[320px]" data-testid={`shared-column-${col.id}`}>
                  <div className="flex items-center gap-2 mb-3 px-1">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: col.color }} />
                    <h3 className="font-semibold text-sm">{col.name}</h3>
                    <span className="text-xs text-muted-foreground ml-auto">{colTasks.length}</span>
                  </div>
                  <div className="space-y-3">
                    {colTasks.map(task => (
                      <SharedTaskCard key={task.id} task={task} columns={columns} categories={data.categories} tags={data.tags} />
                    ))}
                    {colTasks.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground text-sm border border-dashed border-border rounded-xl">
                        No tasks
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {unassigned.length > 0 && (
              <div className="min-w-[320px] max-w-[320px]">
                <div className="flex items-center gap-2 mb-3 px-1">
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                  <h3 className="font-semibold text-sm">Unassigned</h3>
                  <span className="text-xs text-muted-foreground ml-auto">{unassigned.length}</span>
                </div>
                <div className="space-y-3">
                  {unassigned.map(task => (
                    <SharedTaskCard key={task.id} task={task} columns={columns} categories={data.categories} tags={data.tags} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <PoweredByFooter />
      </div>
    );
  }

  return null;
};
